import { useState, useCallback } from "react";

export type ToastType = "success" | "error" | "warning" | "info";

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
}

export interface ErrorModal {
  open: boolean;
  message: string;
}

export function useToast() {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [errorModal, setErrorModal] = useState<ErrorModal>({ open: false, message: "" });

  const addToast = useCallback((message: string, type: ToastType = "info") => {
    const id = Date.now().toString();
    setToasts((prev) => [...prev, { id, message, type }]);

    // Auto remove after 4 seconds
    setTimeout(() => {
      removeToast(id);
    }, 4000);

    return id;
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const error = useCallback((message: string) => {
    setErrorModal({ open: true, message });
    return Date.now().toString();
  }, []);

  const closeErrorModal = useCallback(() => {
    setErrorModal({ open: false, message: "" });
  }, []);

  const success = useCallback((message: string) => addToast(message, "success"), [addToast]);
  const warning = useCallback((message: string) => addToast(message, "warning"), [addToast]);
  const info = useCallback((message: string) => addToast(message, "info"), [addToast]);

  return {
    toasts,
    addToast,
    removeToast,
    error,
    success,
    warning,
    info,
    errorModal,
    closeErrorModal,
  };
}
